import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { useHistory } from 'react-router-dom';
import api from '../../../../services/api';

const MOTIVOS_ENTRADA = [
    { value: 'compra', label: 'Compra' },
    { value: 'devolucao', label: 'Devolução' }
];

const MOTIVOS_AJUSTE = [
    { value: 'ajuste_manual', label: 'Ajuste Manual (contagem física)' },
    { value: 'perda', label: 'Perda/Avaria' }
];

const CreateStockMovement = () => {
    const history = useHistory();

    const [appId, setAppId] = useState(null);
    const [status, setStatus] = useState({ type: '', message: '' });

    const [products, setProducts] = useState([]);
    const [selectedProductId, setSelectedProductId] = useState('');
    const [selectedVariacaoId, setSelectedVariacaoId] = useState('');

    const [warehouses, setWarehouses] = useState([]);
    const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
    const [locations, setLocations] = useState([]);
    const [selectedLocationId, setSelectedLocationId] = useState('');

    const [tipo, setTipo] = useState('entrada');
    const [motivo, setMotivo] = useState('compra');
    const [quantidade, setQuantidade] = useState('');
    const [quantidadeNova, setQuantidadeNova] = useState('');
    const [notas, setNotas] = useState('');
    const [estoqueNoDeposito, setEstoqueNoDeposito] = useState(null);
    const [lotNumber, setLotNumber] = useState('');
    const [lotValidade, setLotValidade] = useState('');
    const [serialNumbersText, setSerialNumbersText] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        (async () => {
            const res = await api.get(`v1/apps/${appId}/products/`);
            setProducts(res.data || []);
        })();
    }, [appId]);

    useEffect(() => {
        if (!appId) return;
        (async () => {
            const res = await api.get(`v1/apps/${appId}/inventory/warehouses/`);
            setWarehouses(res.data || []);
        })();
    }, [appId]);

    useEffect(() => {
        setMotivo(tipo === 'entrada' ? 'compra' : 'ajuste_manual');
    }, [tipo]);

    useEffect(() => {
        setSelectedLocationId('');
        if (!appId || !selectedWarehouseId) {
            setLocations([]);
            return;
        }
        (async () => {
            const res = await api.get(`v1/apps/${appId}/inventory/warehouse-locations/?warehouse=${selectedWarehouseId}`);
            setLocations(res.data || []);
        })();
    }, [appId, selectedWarehouseId]);

    useEffect(() => {
        setEstoqueNoDeposito(null);
        if (!appId || !selectedWarehouseId || !selectedVariacaoId) return;
        (async () => {
            const res = await api.get(`v1/apps/${appId}/inventory/stock/?warehouse=${selectedWarehouseId}`);
            const linha = (res.data.stock || []).find((s) => String(s.produto_variacao) === String(selectedVariacaoId));
            setEstoqueNoDeposito(linha ? linha.estoque : 0);
        })();
    }, [appId, selectedWarehouseId, selectedVariacaoId]);

    const selectedProduct = products.find((p) => String(p.id) === String(selectedProductId));
    const variacoesDoProduto = selectedProduct?.variacoes || [];
    const selectedVariacao = variacoesDoProduto.find((v) => String(v.id) === String(selectedVariacaoId));
    const controleEstoque = selectedVariacao?.controle_estoque || 'none';

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ type: '', message: '' });

        if (!selectedVariacaoId) {
            setStatus({ type: 'error', message: 'Selecione um produto e uma variação' });
            return;
        }
        if (!selectedWarehouseId) {
            setStatus({ type: 'error', message: 'Selecione o depósito onde o movimento acontece' });
            return;
        }

        const payload = {
            produto_variacao: selectedVariacaoId,
            warehouse: selectedWarehouseId,
            location: selectedLocationId || undefined,
            tipo,
            motivo,
            notas
        };

        if (tipo === 'entrada') {
            if (!quantidade || Number(quantidade) <= 0) {
                setStatus({ type: 'error', message: 'Informe uma quantidade maior que zero' });
                return;
            }
            payload.quantidade = Number(quantidade);

            if (controleEstoque === 'lot') {
                if (!lotNumber) {
                    setStatus({ type: 'error', message: 'Informe o número do lote' });
                    return;
                }
                payload.lot_number = lotNumber;
                payload.lot_validade = lotValidade || undefined;
            } else if (controleEstoque === 'serial') {
                const numeros = serialNumbersText
                    .split(/[\n,]/)
                    .map((s) => s.trim())
                    .filter(Boolean);
                if (numeros.length !== Number(quantidade)) {
                    setStatus({
                        type: 'error',
                        message: `Informe exatamente ${quantidade} número(s) de série (um por linha ou separados por vírgula)`
                    });
                    return;
                }
                payload.serial_numbers = numeros;
            }
        } else {
            if (quantidadeNova === '' || Number(quantidadeNova) < 0) {
                setStatus({ type: 'error', message: 'Informe a nova quantidade em estoque (pode ser zero)' });
                return;
            }
            payload.quantidade_nova = Number(quantidadeNova);
        }

        try {
            await api.post(`v1/apps/${appId}/inventory/movements/create/`, payload);
            history.push(`/dashboard/inventory/movements?produto_variacao=${selectedVariacaoId}&warehouse=${selectedWarehouseId}`);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao registrar movimento' });
        }
    };

    return (
        <Box>
            <Typography variant="h3" gutterBottom sx={{ mb: 2 }}>
                Novo Movimento de Estoque
            </Typography>

            <Card>
                <CardContent>
                    {status.message && (
                        <Alert severity={status.type} sx={{ mb: 2 }}>
                            {status.message}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Produto"
                                    fullWidth
                                    required
                                    value={selectedProductId}
                                    onChange={(e) => {
                                        setSelectedProductId(e.target.value);
                                        setSelectedVariacaoId('');
                                    }}
                                >
                                    {products.map((p) => (
                                        <MenuItem key={p.id} value={p.id}>
                                            {p.nome}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Variação"
                                    fullWidth
                                    required
                                    disabled={!selectedProductId}
                                    value={selectedVariacaoId}
                                    onChange={(e) => setSelectedVariacaoId(e.target.value)}
                                >
                                    {variacoesDoProduto.map((v) => (
                                        <MenuItem key={v.id} value={v.id}>
                                            {v.nome_variacao} (estoque total: {v.estoque})
                                            {v.controle_estoque === 'lot' && ' · controle por lote'}
                                            {v.controle_estoque === 'serial' && ' · controle por série'}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Depósito"
                                    fullWidth
                                    required
                                    value={selectedWarehouseId}
                                    onChange={(e) => setSelectedWarehouseId(e.target.value)}
                                >
                                    {warehouses.map((w) => (
                                        <MenuItem key={w.id} value={w.id}>
                                            {w.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Localização (opcional)"
                                    fullWidth
                                    disabled={!selectedWarehouseId}
                                    value={selectedLocationId}
                                    onChange={(e) => setSelectedLocationId(e.target.value)}
                                >
                                    <MenuItem value="">Nenhuma</MenuItem>
                                    {locations.map((l) => (
                                        <MenuItem key={l.id} value={l.id}>
                                            {l.code}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField select label="Tipo de movimento" fullWidth value={tipo} onChange={(e) => setTipo(e.target.value)}>
                                    <MenuItem value="entrada">Entrada</MenuItem>
                                    <MenuItem value="ajuste">Ajuste (contagem física)</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField select label="Motivo" fullWidth value={motivo} onChange={(e) => setMotivo(e.target.value)}>
                                    {(tipo === 'entrada' ? MOTIVOS_ENTRADA : MOTIVOS_AJUSTE).map((m) => (
                                        <MenuItem key={m.value} value={m.value}>
                                            {m.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {tipo === 'entrada' ? (
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Quantidade a adicionar"
                                        type="number"
                                        fullWidth
                                        required
                                        inputProps={{ min: 1 }}
                                        value={quantidade}
                                        onChange={(e) => setQuantidade(e.target.value)}
                                    />
                                </Grid>
                            ) : (
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Nova quantidade no depósito"
                                        type="number"
                                        fullWidth
                                        required
                                        helperText={
                                            estoqueNoDeposito !== null
                                                ? `Estoque atual nesse depósito: ${estoqueNoDeposito}`
                                                : 'Selecione uma variação e um depósito'
                                        }
                                        inputProps={{ min: 0 }}
                                        value={quantidadeNova}
                                        onChange={(e) => setQuantidadeNova(e.target.value)}
                                    />
                                </Grid>
                            )}

                            {tipo === 'entrada' && controleEstoque === 'lot' && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Número do lote"
                                            fullWidth
                                            required
                                            value={lotNumber}
                                            onChange={(e) => setLotNumber(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Validade (opcional)"
                                            type="date"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            value={lotValidade}
                                            onChange={(e) => setLotValidade(e.target.value)}
                                        />
                                    </Grid>
                                </>
                            )}

                            {tipo === 'entrada' && controleEstoque === 'serial' && (
                                <Grid item xs={12}>
                                    <TextField
                                        label="Números de série"
                                        fullWidth
                                        required
                                        multiline
                                        minRows={2}
                                        helperText={`Um número por linha (ou separados por vírgula) - precisa ter exatamente ${quantidade || 0} número(s)`}
                                        value={serialNumbersText}
                                        onChange={(e) => setSerialNumbersText(e.target.value)}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <TextField
                                    label="Notas"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={notas}
                                    onChange={(e) => setNotas(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="flex-end" gap={2}>
                                    <Button onClick={() => history.push('/dashboard/inventory')}>Voltar</Button>
                                    <Button type="submit" variant="contained">
                                        Registrar Movimento
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </Card>
        </Box>
    );
};

export default CreateStockMovement;
